// Custom vendor auth client (NOT Supabase Auth), analog to lib/adminAuth.ts.
// Cookie mode (default): httpOnly session via /api/vendor Nitro proxy.
// Legacy mode: opaque token in localStorage (dev rollback only).

import { isCookieVendorAuthMode, vendorApiUrl } from "@/lib/vendorAuthMode";

export const VENDOR_TOKEN_KEY = "pulse.vendorToken";
export const VENDOR_USER_KEY = "pulse.vendorUser";

export type VendorUser = {
  id: string;
  email: string;
  name: string | null;
};

export type VendorClaimInfo = {
  venueId: string;
  status: "pending" | "approved" | "rejected";
  role: string;
  venueName: string | null;
};

export function getVendorToken(): string | null {
  if (typeof window === "undefined" || isCookieVendorAuthMode()) return null;
  return localStorage.getItem(VENDOR_TOKEN_KEY);
}

export function getVendorUser(): VendorUser | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(VENDOR_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as VendorUser;
  } catch {
    return null;
  }
}

export function setVendorProfile(user: VendorUser): void {
  localStorage.setItem(VENDOR_USER_KEY, JSON.stringify(user));
}

export function setVendorSession(token: string, user: VendorUser): void {
  if (isCookieVendorAuthMode()) {
    setVendorProfile(user);
    return;
  }
  localStorage.setItem(VENDOR_TOKEN_KEY, token);
  localStorage.setItem(VENDOR_USER_KEY, JSON.stringify(user));
}

export function clearVendorSession(): void {
  localStorage.removeItem(VENDOR_USER_KEY);
  if (!isCookieVendorAuthMode()) {
    localStorage.removeItem(VENDOR_TOKEN_KEY);
  }
}

export function isVendorAuthenticated(): boolean {
  if (isCookieVendorAuthMode()) {
    return !!getVendorUser();
  }
  return !!getVendorToken();
}

function supabaseUrl(): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new Error("Missing VITE_SUPABASE_URL");
  return url;
}

function supabaseAnonKey(): string {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) throw new Error("Missing VITE_SUPABASE_ANON_KEY");
  return key;
}

function legacyVendorHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${supabaseAnonKey()}`,
    apikey: supabaseAnonKey(),
  };
}

function cookieFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(input, { ...init, credentials: "include" });
}

async function vendorAuthFetch<T>(payload: Record<string, unknown>): Promise<T> {
  if (isCookieVendorAuthMode()) {
    const action = typeof payload.action === "string" ? payload.action : "";
    if (action === "login") {
      const res = await cookieFetch(vendorApiUrl("/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
        }),
      });
      const body = (await res.json()) as { ok?: boolean; error?: string; vendor?: VendorUser };
      if (!res.ok || !body.ok || !body.vendor) {
        throw new Error(body.error ?? "Request failed");
      }
      return body as T;
    }
    if (action === "register") {
      const res = await cookieFetch(vendorApiUrl("/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
          name: payload.name,
        }),
      });
      const body = (await res.json()) as { ok?: boolean; error?: string; vendor?: VendorUser };
      if (!res.ok || !body.ok || !body.vendor) {
        throw new Error(body.error ?? "Request failed");
      }
      return body as T;
    }
    if (action === "validate") {
      const res = await cookieFetch(vendorApiUrl("/session"), { method: "GET" });
      const body = (await res.json()) as { ok?: boolean; vendor?: VendorUser };
      if (!res.ok || !body.ok || !body.vendor) {
        throw new Error("Invalid session");
      }
      return body as T;
    }
    if (action === "logout") {
      await cookieFetch(vendorApiUrl("/logout"), { method: "POST" });
      return { ok: true } as T;
    }

    const res = await cookieFetch(vendorApiUrl("/proxy/vendor-auth"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = (await res.json()) as { ok?: boolean; error?: string } & Record<string, unknown>;
    if (!res.ok || !body.ok) {
      throw new Error(body.error ?? "Request failed");
    }
    return body as T;
  }

  const res = await fetch(`${supabaseUrl()}/functions/v1/vendor-auth`, {
    method: "POST",
    headers: legacyVendorHeaders(),
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as { ok?: boolean; error?: string } & Record<string, unknown>;
  if (!res.ok || !body.ok) {
    throw new Error(body.error ?? "Request failed");
  }
  return body as T;
}

export async function vendorLogin(email: string, password: string): Promise<VendorUser> {
  const body = await vendorAuthFetch<{ token?: string; vendor: VendorUser }>({
    action: "login",
    email,
    password,
  });
  if (body.token) {
    setVendorSession(body.token, body.vendor);
  } else {
    setVendorProfile(body.vendor);
  }
  return body.vendor;
}

export async function vendorRegister(
  email: string,
  password: string,
  name?: string,
): Promise<VendorUser> {
  const body = await vendorAuthFetch<{ token?: string; vendor: VendorUser }>({
    action: "register",
    email,
    password,
    name: name ?? "",
  });
  if (body.token) {
    setVendorSession(body.token, body.vendor);
  } else {
    setVendorProfile(body.vendor);
  }
  return body.vendor;
}

/** Server-validate the stored session; refreshes the cached vendor profile. */
export async function validateVendorSession(): Promise<boolean> {
  if (isCookieVendorAuthMode()) {
    try {
      const res = await cookieFetch(vendorApiUrl("/session"), { method: "GET" });
      const body = (await res.json()) as { ok?: boolean; vendor?: VendorUser };
      if (!res.ok || !body.ok || !body.vendor) {
        clearVendorSession();
        return false;
      }
      setVendorProfile(body.vendor);
      return true;
    } catch {
      clearVendorSession();
      return false;
    }
  }

  const token = getVendorToken();
  if (!token) return false;
  try {
    const body = await vendorAuthFetch<{ vendor: VendorUser }>({ action: "validate", token });
    setVendorSession(token, body.vendor);
    return true;
  } catch {
    clearVendorSession();
    return false;
  }
}

export async function vendorLogout(): Promise<void> {
  const legacyToken = getVendorToken();
  clearVendorSession();
  try {
    if (isCookieVendorAuthMode()) {
      await cookieFetch(vendorApiUrl("/logout"), { method: "POST" });
    } else if (legacyToken) {
      await vendorAuthFetch({ action: "logout", token: legacyToken });
    }
  } catch {
    /* best-effort */
  }
}

/** venue_claims of the authenticated vendor (service-role read via edge function). */
export async function fetchVendorClaims(): Promise<VendorClaimInfo[]> {
  if (isCookieVendorAuthMode()) {
    const body = await vendorAuthFetch<{ claims: VendorClaimInfo[] }>({ action: "claims" });
    return body.claims ?? [];
  }

  const token = getVendorToken();
  if (!token) return [];
  const body = await vendorAuthFetch<{ claims: VendorClaimInfo[] }>({ action: "claims", token });
  return body.claims ?? [];
}

/** Create a pending venue + registry + claim for the authenticated vendor. */
export async function registerVendorVenue(input: {
  name: string;
  category: string;
  address: string;
  city_key: string;
  website: string;
  description: string;
}): Promise<string> {
  const payload = { action: "register_venue", ...input };
  if (!isCookieVendorAuthMode()) {
    const token = getVendorToken();
    if (!token) throw new Error("Not authenticated");
    Object.assign(payload, { token });
  }
  const body = await vendorAuthFetch<{ venueId: string }>(payload);
  return body.venueId;
}
